import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, qtyInputStep } from '../lib/quantity';
import {
  flavorApi,
  type FlavorMeta,
  type FlavorMixDetail,
  type FlavorMixListRow,
  type FlavorRecipeDetail,
  type FlavorRecipeListRow,
} from '../services/flavor';

type TabKey = 'recipes' | 'mixes';

type RecipeItemDraft = {
  productId: string;
  qtyPerBase: string;
  unitLabel: string;
};

type MixSourceDraft = {
  containerId: string;
  qtyKg: string;
};

const EMPTY_RECIPE_ITEM: RecipeItemDraft = {
  productId: '',
  qtyPerBase: '',
  unitLabel: '',
};

const EMPTY_SOURCE_ROW: MixSourceDraft = {
  containerId: '',
  qtyKg: '',
};

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col rounded border border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" className="btn btn-secondary px-2 py-1" onClick={onClose}>
            Stäng
          </button>
        </div>
        <div className="overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
}

export function FlavorPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('bottling');
  const currentYear = new Date().getFullYear();

  const [tab, setTab] = useState<TabKey>('mixes');
  const [meta, setMeta] = useState<FlavorMeta | null>(null);
  const [recipes, setRecipes] = useState<FlavorRecipeListRow[]>([]);
  const [recipesLoading, setRecipesLoading] = useState(true);
  const [mixesLoading, setMixesLoading] = useState(true);
  const [mixes, setMixes] = useState<FlavorMixListRow[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState<number | null>(null);
  const [recipeName, setRecipeName] = useState('');
  const [recipeBaseHoneyKg, setRecipeBaseHoneyKg] = useState('10');
  const [recipeOutputProductId, setRecipeOutputProductId] = useState('');
  const [recipeNotes, setRecipeNotes] = useState('');
  const [recipeItems, setRecipeItems] = useState<RecipeItemDraft[]>([{ ...EMPTY_RECIPE_ITEM }]);
  const [recipeSaving, setRecipeSaving] = useState(false);
  const [recipeDeleteConfirm, setRecipeDeleteConfirm] = useState(false);

  const [mixDate, setMixDate] = useState(new Date().toISOString().slice(0, 10));
  const [mixRecipeId, setMixRecipeId] = useState('');
  const [mixSiteId, setMixSiteId] = useState('');
  const [mixComment, setMixComment] = useState('');
  const [mixSourceRows, setMixSourceRows] = useState<MixSourceDraft[]>([{ ...EMPTY_SOURCE_ROW }]);
  const [editingMixId, setEditingMixId] = useState<number | null>(null);
  const [mixCreating, setMixCreating] = useState(false);
  const [selectedRecipeDetail, setSelectedRecipeDetail] = useState<FlavorRecipeDetail | null>(null);
  const [selectedRecipeLoading, setSelectedRecipeLoading] = useState(false);

  const [selectedMixId, setSelectedMixId] = useState<number | null>(null);
  const [mixDetail, setMixDetail] = useState<FlavorMixDetail | null>(null);
  const [mixDetailLoading, setMixDetailLoading] = useState(false);
  const [yieldDraft, setYieldDraft] = useState('');
  const [yieldSaving, setYieldSaving] = useState(false);
  const [postingMix, setPostingMix] = useState(false);

  const ingredientProductById = useMemo(() => {
    const map = new Map<number, FlavorMeta['ingredientProducts'][number]>();
    (meta?.ingredientProducts ?? []).forEach((product) => map.set(product.id, product));
    return map;
  }, [meta]);

  const defaultMixSiteId = useMemo(
    () => meta?.sites.find((site) => site.name === 'Lager')?.id ?? meta?.sites[0]?.id ?? null,
    [meta]
  );

  const plannedHoneyKg = useMemo(
    () =>
      Math.round(
        mixSourceRows.reduce((sum, row) => {
          const qty = Number(row.qtyKg);
          return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
        }, 0) * 1000
      ) / 1000,
    [mixSourceRows]
  );

  const localPreview = useMemo(() => {
    if (!selectedRecipeDetail || plannedHoneyKg <= 0 || selectedRecipeDetail.baseHoneyKg <= 0) {
      return null;
    }

    const factor = plannedHoneyKg / selectedRecipeDetail.baseHoneyKg;
    return {
      recipeName: selectedRecipeDetail.name,
      outputProductName: selectedRecipeDetail.outputProductName,
      plannedHoneyKg,
      factor,
      items: selectedRecipeDetail.items.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        unitLabel: item.unitLabel,
        qtyPerBase: item.qtyPerBase,
        plannedQty: Math.round(item.qtyPerBase * factor * 1000) / 1000,
      })),
    };
  }, [plannedHoneyKg, selectedRecipeDetail]);

  const resetRecipeForm = () => {
    setEditingRecipeId(null);
    setRecipeName('');
    setRecipeBaseHoneyKg('10');
    setRecipeOutputProductId('');
    setRecipeNotes('');
    setRecipeItems([{ ...EMPTY_RECIPE_ITEM }]);
    setRecipeDeleteConfirm(false);
  };

  const resetMixForm = () => {
    setEditingMixId(null);
    setMixRecipeId('');
    setMixComment('');
    setMixSourceRows([{ ...EMPTY_SOURCE_ROW }]);
  };

  const loadMeta = async () => {
    setMeta(await flavorApi.meta());
  };

  useEffect(() => {
    if (mixSiteId === '' && defaultMixSiteId !== null) {
      setMixSiteId(String(defaultMixSiteId));
    }
  }, [defaultMixSiteId, mixSiteId]);

  const loadRecipes = async () => {
    setRecipesLoading(true);
    try {
      const response = await flavorApi.listRecipes();
      setRecipes(response.recipes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda recept');
    } finally {
      setRecipesLoading(false);
    }
  };

  const loadMixes = async (year: number) => {
    setMixesLoading(true);
    try {
      const response = await flavorApi.listMixes(year);
      setMixes(response.mixes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda smaksättning');
    } finally {
      setMixesLoading(false);
    }
  };

  const loadMixDetail = async (id: number) => {
    setMixDetailLoading(true);
    try {
      const detail = await flavorApi.getMix(id);
      setMixDetail(detail);
      setYieldDraft(String(detail.yieldKg));
    } catch (error) {
      setMixDetail(null);
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda smaksättning');
    } finally {
      setMixDetailLoading(false);
    }
  };

  const loadSelectedRecipeDetail = async (id: number) => {
    setSelectedRecipeLoading(true);
    try {
      const recipe = await flavorApi.getRecipe(id);
      setSelectedRecipeDetail(recipe);
    } catch (error) {
      setSelectedRecipeDetail(null);
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda recept');
    } finally {
      setSelectedRecipeLoading(false);
    }
  };

  useEffect(() => {
    void Promise.all([loadMeta(), loadRecipes(), loadMixes(selectedYear)]).catch((error) => {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda smaksättning');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadMixes(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedMixId) {
      setMixDetail(null);
      return;
    }
    void loadMixDetail(selectedMixId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMixId]);

  useEffect(() => {
    if (!mixRecipeId) {
      setSelectedRecipeDetail(null);
      return;
    }
    void loadSelectedRecipeDetail(Number(mixRecipeId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixRecipeId]);

  const openNewRecipe = () => {
    resetRecipeForm();
    setRecipeDialogOpen(true);
  };

  const openEditRecipe = async (id: number) => {
    try {
      const recipe = await flavorApi.getRecipe(id);
      setEditingRecipeId(recipe.id);
      setRecipeName(recipe.name);
      setRecipeBaseHoneyKg(String(recipe.baseHoneyKg));
      setRecipeOutputProductId(String(recipe.outputProductId));
      setRecipeNotes(recipe.notes ?? '');
      setRecipeItems(
        recipe.items.map((item) => ({
          productId: String(item.productId),
          qtyPerBase: String(item.qtyPerBase),
          unitLabel: item.unitLabel,
        }))
      );
      setRecipeDeleteConfirm(false);
      setRecipeDialogOpen(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda recept');
    }
  };

  const updateRecipeItem = (index: number, patch: Partial<RecipeItemDraft>) => {
    setRecipeItems((current) =>
      current.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }
        const next = { ...item, ...patch };
        if (patch.productId !== undefined) {
          const product = ingredientProductById.get(Number(patch.productId));
          next.unitLabel = product?.uomLabel ?? '';
        }
        return next;
      })
    );
  };

  const addRecipeItem = () => setRecipeItems((current) => [...current, { ...EMPTY_RECIPE_ITEM }]);
  const removeRecipeItem = (index: number) => setRecipeItems((current) => current.filter((_, itemIndex) => itemIndex !== index));

  const updateSourceRow = (index: number, patch: Partial<MixSourceDraft>) => {
    setMixSourceRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const addSourceRow = () => setMixSourceRows((current) => [...current, { ...EMPTY_SOURCE_ROW }]);
  const removeSourceRow = (index: number) => setMixSourceRows((current) => current.filter((_, rowIndex) => rowIndex !== index));

  const saveRecipe = async () => {
    if (!recipeName.trim() || Number(recipeBaseHoneyKg) <= 0 || !recipeOutputProductId) {
      toast.error('Namn, bashonung och slutprodukt krävs');
      return;
    }

    const items = recipeItems
      .filter((item) => item.productId !== '' && item.qtyPerBase.trim() !== '')
      .map((item) => ({
        productId: Number(item.productId),
        qtyPerBase: Number(item.qtyPerBase),
        unitLabel: item.unitLabel,
      }));

    if (items.length === 0) {
      toast.error('Minst en ingrediensrad krävs');
      return;
    }

    setRecipeSaving(true);
    try {
      const payload = {
        name: recipeName.trim(),
        baseHoneyKg: Number(recipeBaseHoneyKg),
        outputProductId: Number(recipeOutputProductId),
        notes: recipeNotes.trim(),
        items,
      };

      if (editingRecipeId) {
        await flavorApi.updateRecipe(editingRecipeId, payload);
        toast.success('Recept uppdaterat');
      } else {
        await flavorApi.createRecipe(payload);
        toast.success('Recept skapat');
      }

      setRecipeDialogOpen(false);
      resetRecipeForm();
      await loadRecipes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara recept');
    } finally {
      setRecipeSaving(false);
    }
  };

  const deleteRecipe = async () => {
    if (!editingRecipeId) {
      return;
    }
    try {
      await flavorApi.deleteRecipe(editingRecipeId);
      toast.success('Recept inaktiverat');
      setRecipeDialogOpen(false);
      resetRecipeForm();
      await loadRecipes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte inaktivera recept');
    }
  };

  const createMix = async () => {
    const sourceContainers = mixSourceRows
      .filter((row) => row.containerId.trim() !== '' && row.qtyKg.trim() !== '')
      .map((row) => ({
        containerId: Number(row.containerId),
        qtyKg: Number(row.qtyKg),
      }))
      .filter((row) => row.containerId > 0 && row.qtyKg > 0);

    if (!mixRecipeId || !mixSiteId || sourceContainers.length === 0 || plannedHoneyKg <= 0) {
      toast.error('Recept, lagerplats och minst en källhink krävs');
      return;
    }

    setMixCreating(true);
    try {
      if (editingMixId) {
        await flavorApi.updateMix(editingMixId, {
          recipeId: Number(mixRecipeId),
          siteId: Number(mixSiteId),
          sourceContainers,
          mixDate,
          comment: mixComment.trim(),
        });
        toast.success('Smaksättning uppdaterad');
        await loadMixes(selectedYear);
        setSelectedMixId(editingMixId);
      } else {
        const created = await flavorApi.createMix({
          recipeId: Number(mixRecipeId),
          siteId: Number(mixSiteId),
          sourceContainers,
          mixDate,
          comment: mixComment.trim(),
        });
        toast.success('Smaksättning skapad');
        await loadMixes(selectedYear);
        setSelectedMixId(created.id);
      }
      resetMixForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : editingMixId ? 'Kunde inte uppdatera smaksättning' : 'Kunde inte skapa smaksättning');
    } finally {
      setMixCreating(false);
    }
  };

  const startEditMix = (detail: FlavorMixDetail) => {
    setEditingMixId(detail.id);
    setMixDate(String(detail.mixDate).slice(0, 10));
    setMixRecipeId(String(detail.recipeId));
    setMixSiteId(String(detail.siteId));
    setMixComment(detail.comment ?? '');
    setMixSourceRows(
      detail.sourceContainers.length > 0
        ? detail.sourceContainers.map((source) => ({
            containerId: String(source.containerId),
            qtyKg: String(source.qtyKg),
          }))
        : [{ ...EMPTY_SOURCE_ROW }]
    );
    setSelectedMixId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveYield = async () => {
    if (!mixDetail) {
      return;
    }

    const nextYield = Number(yieldDraft);
    if (nextYield <= 0 || nextYield > mixDetail.plannedHoneyKg) {
      toast.error('Yield måste vara större än 0 och högst planerad mängd');
      return;
    }

    setYieldSaving(true);
    try {
      await flavorApi.updateYield(mixDetail.id, { yieldKg: nextYield });
      toast.success('Yield sparad');
      await loadMixDetail(mixDetail.id);
      await loadMixes(selectedYear);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara yield');
    } finally {
      setYieldSaving(false);
    }
  };

  const postMix = async () => {
    if (!mixDetail) {
      return;
    }

    setPostingMix(true);
    try {
      await flavorApi.postMix(mixDetail.id);
      toast.success('Smaksättning lagerförd');
      await loadMixDetail(mixDetail.id);
      await loadMixes(selectedYear);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte lagerföra smaksättning');
    } finally {
      setPostingMix(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">Smaksatt honung</h1>
        <p className="text-sm text-gray-600">Recept och smaksättningstillfällen.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={`btn ${tab === 'mixes' ? 'btn-primary' : 'btn-secondary'} px-3 py-1.5`} onClick={() => setTab('mixes')}>
            Smaksättning
          </button>
          <button type="button" className={`btn ${tab === 'recipes' ? 'btn-primary' : 'btn-secondary'} px-3 py-1.5`} onClick={() => setTab('recipes')}>
            Recept
          </button>
        </div>

        {tab === 'mixes' && (
          <div className="flex items-center gap-1">
            <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => value - 1)}>
              PREV
            </button>
            <span className="min-w-16 text-center text-sm font-semibold">{selectedYear}</span>
            {selectedYear < currentYear && (
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => Math.min(currentYear, value + 1))}>
                NEXT
              </button>
            )}
          </div>
        )}
      </div>

      {tab === 'recipes' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Recept</h2>
            {writable && (
              <button type="button" className="btn btn-primary px-2 py-1" onClick={openNewRecipe}>
                Nytt recept
              </button>
            )}
          </div>

          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-gray-200 bg-gray-100">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Namn</th>
                    <th className="px-2 py-1.5 text-left">Bas honung</th>
                    <th className="px-2 py-1.5 text-left">Slutprodukt</th>
                    <th className="px-2 py-1.5 text-left">Ingredienser</th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.map((recipe) => (
                    <tr key={recipe.id} className="cursor-pointer border-t border-gray-100 hover:bg-orange-50" onClick={() => void openEditRecipe(recipe.id)}>
                      <td className="px-2 py-1.5">{recipe.name}</td>
                      <td className="px-2 py-1.5">{formatQty(recipe.baseHoneyKg, 'kg')} kg</td>
                      <td className="px-2 py-1.5">{recipe.outputProductName}</td>
                      <td className="px-2 py-1.5">{recipe.itemCount}</td>
                    </tr>
                  ))}
                  {!recipesLoading && recipes.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-2 py-4 text-gray-500">
                        Inga recept ännu.
                      </td>
                    </tr>
                  )}
                  {recipesLoading && (
                    <tr>
                      <td colSpan={4} className="px-2 py-4 text-gray-500">
                        Laddar recept...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-3 rounded border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold">{editingMixId ? `Redigera utkast #${editingMixId}` : 'Ny smaksättning'}</h2>
              <div className="text-sm text-gray-600">{localPreview ? `Faktor ${formatQty(localPreview.factor)}` : 'Välj recept och fyll i källhinkar'}</div>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <label className="block text-sm">
                Datum
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={mixDate} onChange={(event) => setMixDate(event.target.value)} />
              </label>
              <label className="block text-sm">
                Recept
                <select className="mt-1 w-full" value={mixRecipeId} onChange={(event) => setMixRecipeId(event.target.value)}>
                  <option value="">Välj recept</option>
                  {recipes.map((recipe) => (
                    <option key={recipe.id} value={recipe.id}>
                      {recipe.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                Lagerplats
                <select className="mt-1 w-full" value={mixSiteId} onChange={(event) => setMixSiteId(event.target.value)}>
                  <option value="">Välj lagerplats</option>
                  {(meta?.sites ?? []).map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="space-y-2 rounded border border-gray-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">Källhinkar</div>
                {writable && (
                  <button type="button" className="btn btn-secondary px-2 py-1" onClick={addSourceRow}>
                    Lägg till rad
                  </button>
                )}
              </div>

              {mixSourceRows.map((row, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-2 md:grid-cols-[160px_180px_110px]">
                  <label className="block text-sm">
                    Hink-ID
                    <input
                      type="number"
                      min="1"
                      className="mt-1 w-full"
                      value={row.containerId}
                      onChange={(event) => updateSourceRow(index, { containerId: event.target.value })}
                    />
                  </label>
                  <label className="block text-sm">
                    Honung (kg)
                    <input
                      type="number"
                      min="0"
                      step={qtyInputStep('kg')}
                      className="mt-1 w-full"
                      value={row.qtyKg}
                      onChange={(event) => updateSourceRow(index, { qtyKg: event.target.value })}
                    />
                  </label>
                  <div className="flex items-end">
                    <button type="button" className="btn btn-danger px-2 py-1" onClick={() => removeSourceRow(index)} disabled={mixSourceRows.length <= 1}>
                      Ta bort
                    </button>
                  </div>
                </div>
              ))}

              <div className="text-sm text-gray-600">Planerad honung: {plannedHoneyKg > 0 ? `${formatQty(plannedHoneyKg, 'kg')} kg` : '-'}</div>
            </div>

            <label className="block text-sm">
              Kommentar
              <textarea className="mt-1 min-h-[70px] w-full" value={mixComment} onChange={(event) => setMixComment(event.target.value)} />
            </label>

            <div className="rounded border border-gray-200">
              <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium">Förhandsvisning</div>
              <div className="space-y-3 p-3">
                {selectedRecipeLoading && <p className="text-sm text-gray-500">Laddar recept...</p>}
                {!selectedRecipeLoading && !localPreview && <p className="text-sm text-gray-500">Förhandsvisning visas när recept och källhinkar är ifyllda.</p>}
                {localPreview && (
                  <>
                    <div className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
                      <div>Recept: {localPreview.recipeName}</div>
                      <div>Slutprodukt: {localPreview.outputProductName}</div>
                      <div>Planerad honung: {formatQty(localPreview.plannedHoneyKg, 'kg')} kg</div>
                      <div>Faktor: {formatQty(localPreview.factor)}</div>
                    </div>
                    <div className="overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Ingrediens</th>
                            <th className="px-2 py-1.5 text-left">Per bas</th>
                            <th className="px-2 py-1.5 text-left">Behov</th>
                          </tr>
                        </thead>
                        <tbody>
                          {localPreview.items.map((item) => (
                            <tr key={item.productId} className="border-t border-gray-100">
                              <td className="px-2 py-1.5">{item.productName}</td>
                              <td className="px-2 py-1.5">
                                {formatQty(item.qtyPerBase, item.unitLabel)} {item.unitLabel}
                              </td>
                              <td className="px-2 py-1.5">
                                {formatQty(item.plannedQty, item.unitLabel)} {item.unitLabel}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            {writable && (
              <div className="flex justify-end gap-2">
                {editingMixId && (
                  <button type="button" className="btn btn-secondary px-3 py-1.5" onClick={resetMixForm} disabled={mixCreating}>
                    Avbryt redigering
                  </button>
                )}
                <button type="button" className="btn btn-success px-3 py-1.5" onClick={createMix} disabled={mixCreating}>
                  {mixCreating ? 'Sparar...' : editingMixId ? 'Spara ändringar' : 'Spara smaksättning'}
                </button>
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 24rem)' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-gray-200 bg-gray-100">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Datum</th>
                    <th className="px-2 py-1.5 text-left">Recept</th>
                    <th className="px-2 py-1.5 text-left">Källa</th>
                    <th className="px-2 py-1.5 text-left">Planerad</th>
                    <th className="px-2 py-1.5 text-left">Yield</th>
                    <th className="px-2 py-1.5 text-left">Resultathink</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {mixes.map((mix) => (
                    <tr key={mix.id} className="cursor-pointer border-t border-gray-100 hover:bg-orange-50" onClick={() => setSelectedMixId(mix.id)}>
                      <td className="px-2 py-1.5">{String(mix.mixDate).slice(0, 10)}</td>
                      <td className="px-2 py-1.5">{mix.recipeName}</td>
                      <td className="px-2 py-1.5">
                        {mix.sourceCount > 1
                          ? `${formatContainerDisplayId(mix.sourceContainerId, mix.sourceContainerCode)} (${mix.sourceCount} hinkar)`
                          : formatContainerDisplayId(mix.sourceContainerId, mix.sourceContainerCode)}
                      </td>
                      <td className="px-2 py-1.5">{formatQty(mix.plannedHoneyKg, 'kg')} kg</td>
                      <td className="px-2 py-1.5">{formatQty(mix.yieldKg, 'kg')} kg</td>
                      <td className="px-2 py-1.5">
                        {mix.outputContainerId ? formatContainerDisplayId(mix.outputContainerId, mix.outputContainerCode) : 'Ej skapad'}
                      </td>
                      <td className="px-2 py-1.5">{mix.status === 'POSTED' ? 'Lagerförd' : 'Utkast'}</td>
                    </tr>
                  ))}
                  {!mixesLoading && mixes.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-2 py-4 text-gray-500">
                        Inga smaksättningar för {selectedYear}.
                      </td>
                    </tr>
                  )}
                  {mixesLoading && (
                    <tr>
                      <td colSpan={7} className="px-2 py-4 text-gray-500">
                        Laddar smaksättningar...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {recipeDialogOpen && (
        <Dialog title={editingRecipeId ? 'Redigera recept' : 'Nytt recept'} onClose={() => setRecipeDialogOpen(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <label className="block text-sm">
                Namn
                <input className="mt-1 w-full" value={recipeName} onChange={(event) => setRecipeName(event.target.value)} />
              </label>
              <label className="block text-sm">
                Bas honung (kg)
                <input type="number" min="0" step={qtyInputStep('kg')} className="mt-1 w-full" value={recipeBaseHoneyKg} onChange={(event) => setRecipeBaseHoneyKg(event.target.value)} />
              </label>
              <label className="block text-sm">
                Slutprodukt
                <select className="mt-1 w-full" value={recipeOutputProductId} onChange={(event) => setRecipeOutputProductId(event.target.value)}>
                  <option value="">Välj produkt</option>
                  {(meta?.outputProducts ?? []).map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block text-sm">
              Instruktioner
              <textarea className="mt-1 min-h-[70px] w-full" value={recipeNotes} onChange={(event) => setRecipeNotes(event.target.value)} />
            </label>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Ingredienser</div>
                {writable && (
                  <button type="button" className="btn btn-secondary px-2 py-1" onClick={addRecipeItem}>
                    Lägg till rad
                  </button>
                )}
              </div>

              {recipeItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-2 md:grid-cols-[minmax(0,1fr)_160px_120px_110px]">
                  <label className="block text-sm">
                    Smaksättare
                    <select className="mt-1 w-full" value={item.productId} onChange={(event) => updateRecipeItem(index, { productId: event.target.value })}>
                      <option value="">Välj produkt</option>
                      {(meta?.ingredientProducts ?? []).map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    Mängd per bas
                    <input type="number" min="0" step="0.0001" className="mt-1 w-full" value={item.qtyPerBase} onChange={(event) => updateRecipeItem(index, { qtyPerBase: event.target.value })} />
                  </label>
                  <label className="block text-sm">
                    Enhet
                    <input className="mt-1 w-full bg-gray-50" value={item.unitLabel} readOnly />
                  </label>
                  <div className="flex items-end">
                    <button type="button" className="btn btn-danger px-2 py-1" onClick={() => removeRecipeItem(index)} disabled={recipeItems.length <= 1}>
                      Ta bort
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-between gap-2">
              <div className="flex items-center gap-2">
                {editingRecipeId && writable && !recipeDeleteConfirm && (
                  <button type="button" className="btn btn-danger px-2 py-1" onClick={() => setRecipeDeleteConfirm(true)}>
                    Inaktivera recept
                  </button>
                )}
                {editingRecipeId && writable && recipeDeleteConfirm && (
                  <>
                    <button type="button" className="btn btn-danger px-2 py-1" onClick={deleteRecipe}>
                      Bekräfta inaktivering
                    </button>
                    <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setRecipeDeleteConfirm(false)}>
                      Avbryt
                    </button>
                  </>
                )}
              </div>

              {writable && (
                <button type="button" className="btn btn-success px-3 py-1.5" onClick={saveRecipe} disabled={recipeSaving}>
                  {recipeSaving ? 'Sparar...' : 'Spara recept'}
                </button>
              )}
            </div>
          </div>
        </Dialog>
      )}

      {selectedMixId && (
        <Dialog title={mixDetail ? `${mixDetail.recipeName} · ${String(mixDetail.mixDate).slice(0, 10)}` : `Smaksättning #${selectedMixId}`} onClose={() => setSelectedMixId(null)}>
          {mixDetailLoading && <p className="text-sm text-gray-500">Laddar detalj...</p>}

          {mixDetail && !mixDetailLoading && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="grid grid-cols-1 gap-1 text-sm text-gray-700 md:grid-cols-2 md:gap-x-6">
                  <div>Datum: {String(mixDetail.mixDate).slice(0, 10)}</div>
                  <div>Lagerplats: {mixDetail.siteName}</div>
                  <div>Källhink: {formatContainerDisplayId(mixDetail.sourceContainerId, mixDetail.sourceContainerCode)}</div>
                  <div>Källprodukt: {mixDetail.sourceProductName}</div>
                  <div>Recept: {mixDetail.recipeName}</div>
                  <div>Slutprodukt: {mixDetail.outputProductName}</div>
                </div>
                <span className={`rounded px-2 py-1 text-xs ${mixDetail.status === 'POSTED' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                  {mixDetail.status === 'POSTED' ? 'Lagerförd' : 'Utkast'}
                </span>
              </div>

              {mixDetail.comment && <div className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">{mixDetail.comment}</div>}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-sm text-gray-500">Planerad honung</div>
                  <div className="mt-1 text-lg font-semibold">{formatQty(mixDetail.plannedHoneyKg, 'kg')} kg</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-sm text-gray-500">Yield</div>
                  {mixDetail.status === 'DRAFT' && writable ? (
                    <div className="mt-2 flex gap-2">
                      <input type="number" min="0" max={mixDetail.plannedHoneyKg} step={qtyInputStep('kg')} className="w-full" value={yieldDraft} onChange={(event) => setYieldDraft(event.target.value)} />
                      <button type="button" className="btn btn-secondary px-2 py-1" onClick={saveYield} disabled={yieldSaving}>
                        {yieldSaving ? 'Sparar...' : 'Spara'}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-1 text-lg font-semibold">{formatQty(mixDetail.yieldKg, 'kg')} kg</div>
                  )}
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-sm text-gray-500">Spill</div>
                  <div className="mt-1 text-lg font-semibold">{formatQty(mixDetail.spillKg, 'kg')} kg</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">{mixDetail.sourceContainers.length > 1 ? 'Ursprungshinkar' : 'Källhink'}</div>
                <div className="overflow-hidden rounded border border-gray-200">
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-200 bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Hink</th>
                          <th className="px-2 py-1.5 text-left">Produkt</th>
                          <th className="px-2 py-1.5 text-left">Kg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mixDetail.sourceContainers.map((source) => (
                          <tr key={`${source.containerId}-${source.sortOrder}`} className="border-t border-gray-100">
                            <td className="px-2 py-1.5">{formatContainerDisplayId(source.containerId, source.containerCode)}</td>
                            <td className="px-2 py-1.5">{source.productName}</td>
                            <td className="px-2 py-1.5">{formatQty(source.qtyKg, 'kg')} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Ingredienslista</div>
                <div className="overflow-hidden rounded border border-gray-200">
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-200 bg-gray-100">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Ingrediens</th>
                          <th className="px-2 py-1.5 text-left">Planerat uttag</th>
                          <th className="px-2 py-1.5 text-left">Enhet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mixDetail.items.map((item) => (
                          <tr key={item.id} className="border-t border-gray-100">
                            <td className="px-2 py-1.5">{item.productName}</td>
                            <td className="px-2 py-1.5">{formatQty(item.plannedQty, item.unitLabel)}</td>
                            <td className="px-2 py-1.5">{item.unitLabel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-sm text-gray-500">Resultathink</div>
                  <div className="mt-1 text-base font-semibold">
                    {mixDetail.outputContainerId ? formatContainerDisplayId(mixDetail.outputContainerId, mixDetail.outputContainerCode) : 'Inte skapad än'}
                  </div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-sm text-gray-500">Lagertransaktion</div>
                  <div className="mt-1 text-base font-semibold">{mixDetail.inventoryTxId ?? 'Inte skapad än'}</div>
                </div>
              </div>

              {mixDetail.status === 'POSTED' && mixDetail.postedAt && (
                <div className="text-sm text-gray-600">Lagerförd: {String(mixDetail.postedAt).slice(0, 16).replace('T', ' ')}</div>
              )}

              {writable && mixDetail.status === 'DRAFT' && (
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn btn-secondary px-3 py-1.5" onClick={() => startEditMix(mixDetail)} disabled={postingMix}>
                    Redigera
                  </button>
                  <button type="button" className="btn btn-success px-3 py-1.5" onClick={postMix} disabled={postingMix}>
                    {postingMix ? 'Lagerför...' : 'Lagerför'}
                  </button>
                </div>
              )}
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}
